import { Injectable } from '@angular/core';
import { unitsLib } from './unit-library';
import { StorageKeys, StoreService } from './store.service';
import { UserUnitsModel } from '../components/user-units/user-units.model';

/**
 * Service for centralized unit symbol lookup
 * Provides unit symbols based on user preferences
 */
@Injectable({
    providedIn: 'root'
})
export class UnitService {
    private userUnits: UserUnitsModel | null = null;

    constructor(private storeService: StoreService) {
        this.loadUserUnits();
    }

    /**
     * Load user units from store
     * Called on service initialization and can be called manually if units change
     */
    private async loadUserUnits(): Promise<void> {
        this.userUnits = await this.storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    }

    /**
     * Get the display symbol for a unit type based on user preferences
     * 
     * @param unitType - The type of unit (e.g., 'temperature', 'pressure', 'longLengths')
     * @returns The symbol to display (e.g., '°F', 'psi', 'ft')
     * 
     * Examples:
     * - getUnitSymbol('temperature') → '°F' (if user selected API units)
     * - getUnitSymbol('pressure') → 'psig' (if user selected API units)
     * - getUnitSymbol('temperatureGradient') → '°F/100ft' (looks up in library)
     */
    getUnitSymbol(unitType: keyof UserUnitsModel): string {
        if (!this.userUnits) {
            // Fallback to defaults if units not loaded yet
            this.userUnits = new UserUnitsModel();
        }

        const selectedUnit = this.userUnits[unitType] as string;
        if (!selectedUnit) {
            return '';
        }

        // Check if this unit type requires library lookup
        const libraryEntry = unitsLib[selectedUnit];
        if (libraryEntry && libraryEntry.symbol) {
            // Unit has symbol in library (e.g., temperatureGradient: '∆°F/ft' → '°F/100ft')
            return libraryEntry.symbol;
        }

        // Unit value IS the symbol (e.g., temperature: '°F' → '°F')
        return selectedUnit;
    }

    /**
     * Get the full label for a unit type
     * 
     * @param unitType - The type of unit
     * @returns The full label (e.g., 'API (°F)')
     */
    getUnitLabel(unitType: keyof UserUnitsModel): string {
        if (!this.userUnits) {
            this.userUnits = new UserUnitsModel();
        }

        const selectedUnit = this.userUnits[unitType] as string;
        if (!selectedUnit) {
            return '';
        }

        const libraryEntry = unitsLib[selectedUnit];
        if (libraryEntry && libraryEntry.label) {
            return libraryEntry.label;
        }

        return selectedUnit;
    }

    /**
     * Refresh user units from store
     * Call this if user changes their unit preferences
     */
    async refresh(): Promise<void> {
        await this.loadUserUnits();
    }

    /**
     * Get current user units
     */
    getUserUnits(): UserUnitsModel {
        return this.userUnits || new UserUnitsModel();
    }
}
